import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Send, Sparkles, X } from 'lucide-react';

import {
  sendAssistantChat,
  useAssistantConfig,
  type ChatMessage,
  type PendingAction,
} from '../../lib/assistant';
import { useAiCredits, type UsageMeta } from '../../lib/aiCredits';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { Spinner } from '../ui';

/**
 * AssistantWidget — bot de ayuda IA (Gemini) flotante en la app de empresa.
 *
 * Sólo se renderiza si el admin lo habilitó y hay credencial cargada. Chat
 * stateless (historial completo por turno). Las escrituras vuelven como `pending`
 * → tarjeta de confirmación. Consumo: N consultas gratis/día por usuario → luego
 * créditos de la empresa; al agotarse, CTA para comprar (/facturación).
 */
export function AssistantWidget() {
  const { data } = useAssistantConfig();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [usage, setUsage] = useState<UsageMeta | null>(null);
  const [noCredits, setNoCredits] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Balance/cupo para el contador (se refresca al abrir; se sobreescribe con el
  // `usage` que devuelve cada turno del chat).
  const creditsQuery = useAiCredits(open);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pending, loading, noCredits]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (!data?.enabled) return null;

  const freeRemaining = usage?.free_remaining ?? creditsQuery.data?.free_remaining ?? null;
  const balance = usage?.balance ?? creditsQuery.data?.balance_total ?? null;

  const suggestions = [
    t('assistant.sug.pending'),
    t('assistant.sug.progress'),
    t('assistant.sug.create'),
  ];

  function goBuy() {
    setOpen(false);
    navigate('/creditos');
  }

  function handleError(e: unknown, next: ChatMessage[]) {
    if (e instanceof ApiError && e.code === 'assistant_no_credits') {
      setNoCredits(true);
      setMessages([...next, { role: 'assistant', content: e.message }]);
      return;
    }
    setMessages([...next, { role: 'assistant', content: t('assistant.error') }]);
  }

  async function send(text: string) {
    const clean = text.trim();
    if (clean === '' || loading || noCredits) return;
    const next: ChatMessage[] = [...messages, { role: 'user', content: clean }];
    setMessages(next);
    setInput('');
    setPending(null);
    setLoading(true);
    try {
      const res = await sendAssistantChat(next);
      if (res.usage) setUsage(res.usage);
      const reply = res.reply?.trim();
      setMessages([
        ...next,
        { role: 'assistant', content: reply || (res.pending ? t('assistant.proposed') : t('assistant.error')) },
      ]);
      if (res.pending) setPending(res.pending);
    } catch (e) {
      handleError(e, next);
    } finally {
      setLoading(false);
    }
  }

  async function confirm() {
    if (!pending || loading) return;
    const action = pending;
    setPending(null);
    setLoading(true);
    try {
      const res = await sendAssistantChat(messages, { tool: action.tool, args: action.args });
      if (res.usage) setUsage(res.usage);
      setMessages((m) => [...m, { role: 'assistant', content: res.reply?.trim() || t('assistant.done') }]);
      if (res.pending) {
        setPending(res.pending);
      } else {
        void qc.invalidateQueries();
      }
    } catch (e) {
      handleError(e, messages);
    } finally {
      setLoading(false);
    }
  }

  function cancel() {
    setPending(null);
    setMessages((m) => [...m, { role: 'assistant', content: t('assistant.cancelled') }]);
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t('assistant.open')}
          className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-brand-ink shadow-lift transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          <Sparkles size={24} />
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-40 flex h-[min(600px,calc(100dvh-2.5rem))] w-[min(400px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-lift">
          {/* Cabecera */}
          <header className="flex items-center justify-between gap-2 border-b border-line bg-surface-2 px-4 py-3">
            <div className="flex items-center gap-2 overflow-hidden">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand text-brand-ink">
                <Sparkles size={16} />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{t('assistant.title')}</p>
                <p className="truncate text-[11px] text-muted">{t('assistant.subtitle')}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t('assistant.close')}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-token text-muted transition-colors hover:bg-surface hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <X size={18} />
            </button>
          </header>

          {/* Conversación */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-ink">{t('assistant.greeting')}</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void send(s)}
                      className="rounded-full border border-line bg-surface-2 px-3 py-1.5 text-xs text-ink transition-colors hover:border-brand hover:text-brand"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm',
                    m.role === 'user'
                      ? 'rounded-br-sm bg-brand text-brand-ink'
                      : 'rounded-bl-sm bg-surface-2 text-ink',
                  )}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {/* Tarjeta de confirmación de una acción de escritura */}
            {pending && !loading && (
              <div className="rounded-token border border-sun/40 bg-sun/10 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted">
                  {t('assistant.confirmTitle')}
                </p>
                <p className="mt-1 text-sm font-medium text-ink">{pending.summary}</p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => void confirm()}
                    className="rounded-token bg-brand px-3 py-1.5 text-sm font-medium text-brand-ink transition-colors hover:bg-brand-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  >
                    {t('assistant.confirm')}
                  </button>
                  <button
                    type="button"
                    onClick={cancel}
                    className="rounded-token border border-line px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  >
                    {t('assistant.cancel')}
                  </button>
                </div>
              </div>
            )}

            {/* Sin cupo ni créditos → CTA de compra (personal; cualquiera puede) */}
            {noCredits && (
              <div className="rounded-token border border-brand/40 bg-brand/10 p-3">
                <p className="text-sm font-medium text-ink">{t('assistant.buyTitle')}</p>
                <button
                  type="button"
                  onClick={goBuy}
                  className="mt-2 rounded-token bg-brand px-3 py-1.5 text-sm font-medium text-brand-ink transition-colors hover:bg-brand-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  {t('assistant.buyCta')}
                </button>
              </div>
            )}

            {loading && (
              <div className="flex items-center gap-2 text-sm text-muted">
                <Spinner className="h-4 w-4" />
                {t('assistant.thinking')}
              </div>
            )}
          </div>

          {/* Entrada */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void send(input);
            }}
            className="border-t border-line p-3"
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void send(input);
                  }
                }}
                rows={1}
                disabled={noCredits}
                placeholder={t('assistant.placeholder')}
                className="max-h-32 min-h-[2.5rem] flex-1 resize-none rounded-token border border-line bg-surface-2 px-3 py-2 text-sm text-ink placeholder:text-muted focus-visible:border-brand focus-visible:outline-none disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={loading || noCredits || input.trim() === ''}
                aria-label={t('assistant.send')}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-token bg-brand text-brand-ink transition-colors hover:bg-brand-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-40"
              >
                <Send size={18} />
              </button>
            </div>
            {/* Contador de cupo/créditos */}
            {(freeRemaining !== null || balance !== null) && (
              <p className="mt-2 text-center text-[11px] text-muted">
                {t('assistant.counter', { n: String(freeRemaining ?? 0), c: String(balance ?? 0) })}
              </p>
            )}
            <p className="mt-1 text-center text-[10px] text-muted">{t('assistant.disclaimer')}</p>
          </form>
        </div>
      )}
    </>
  );
}
